import { format } from "date-fns";
import { ApplicationDataState as ApplicationDronesState } from "@pages/partners/items/drones/form/types";
import { ApplicationDataState as ApplicationUralchemState } from "@pages/partners/items/uralchem/form/types";
import { first, isNil } from "lodash";
import axios from "axios";
import xhr from "@infobis/api-module";

interface UralchemRequestValues {
    area: string
    fieldsNumber: string
    needSamplingService: string
    organizationAddress: string
    organizationEmail: string
    organizationPhone: string
    organizationTitle: string
    resultsDate: string
    samplesNumber: string
    soilAnalysisType: string
    analysisObject: string
}

interface DroneRequestValues {
    startDate: string
    finishDate: string
    geoZoneId: string
    geoZoneTitle: string
    geoZoneArea: string
    workId: string
    workTitle: string
    goodId: string
    goodTitle: string
    goodExchangePlace: string
    goodExchangePlaceGeoPointJson: string
    goodExchangeTime: string
    customerName: string
    customerRequisites: string
    customerInn: string
    customerPhone: string
    customerEmail: string
    price?: string
    keyWord: string
    comment?: string
}

export default class ApplicationService {

    static parsePhone = (phone: string) => {
        return phone.replace(/\+\d/, (countryCode) => String(Number(countryCode) + 1)).replace(/\s/g, '-');
    }

    static transformDateToServerFormat = (date: [Date | string], withTime?: boolean) => {
        const parsedDate = new Date(first(date)!);
        const dateStringWithoutTime = `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`;
        return dateStringWithoutTime + (withTime
            ? `T${String(parsedDate.getHours()).padStart(2, '0')}:${String(parsedDate.getMinutes()).padStart(2, '0')}:${String(parsedDate.getSeconds()).padStart(2, '0')}.000Z`
            : `T00:00:00.000Z`);
    }

    static transformDronesData(data: Required<ApplicationDronesState & {square: string}>) {
        const parseDate = (date: Date, withTime?: boolean) => {
            const pattern = withTime ? 'dd.MM.yyyy hh:mm:ss' : 'dd.MM.yyyy';
            return format(date, pattern);
        }
        const getJsonStringPolygon = (geoZone: ApplicationDronesState['polygonJson']) => {
            return JSON.stringify(geoZone!.geometry.coordinates[0].map(([lng, lat]: [number, number]) => ({lat, lng})));
        }

        const preparedData = {
            start_date: parseDate(first(data.startDate)!),
            finish_date: parseDate(first(data.finishDate)!),
            square: data.square,
            polygon_json: getJsonStringPolygon(data.polygonJson),
            job_vid: data.jobVid!.title,
            szr: data.szr!.title,
            customer_name: data.customerName,
            customer_rekv: data.customerRekv,
            customer_inn: data.customerInn,
            customer_tlf: ApplicationService.parsePhone(data.customerTlf!),
            customer_email: data.customerEmail,
            price: data.price,
            szr_exchange_place: data.szrExchangePlace,
            szr_exchange_place_geo_point_json: data.szrExchangePlaceGeoPointJson,
            szr_exchange_time: parseDate(first(data.szrExchangeTime)!, true),
            key_word: data.keyWord,
            comment: data.comment,
        };

        const toEmail: DroneRequestValues = {
            startDate: ApplicationService.transformDateToServerFormat(data.startDate),
            finishDate: ApplicationService.transformDateToServerFormat(data.finishDate),
            geoZoneId: data.polygonJson.glid,
            geoZoneTitle: data.polygonJson.name,
            geoZoneArea: data.square,
            workId: String(data.jobVid.id),
            workTitle: data.jobVid.title,
            goodId: String(data.szr.id),
            goodTitle: data.szr.title,
            customerName: data.customerName,
            customerRequisites: data.customerRekv,
            customerInn: data.customerInn,
            customerPhone: ApplicationService.parsePhone(data.customerTlf!),
            customerEmail: data.customerEmail,
            price: isNil(data.price) ? data.price : String(data.price),
            goodExchangePlace: data.szrExchangePlace,
            goodExchangePlaceGeoPointJson: data.szrExchangePlaceGeoPointJson,
            goodExchangeTime: ApplicationService.transformDateToServerFormat(data.szrExchangeTime, true),
            keyWord: data.keyWord,
            comment: data.comment,
        }

        const formData = new FormData();
        Object.entries(preparedData).forEach(([key, value]) => {
            if (!isNil(value)) formData.append(key, String(value));
        });

        return {objectData: toEmail, formData};
    }

    static async sendToEmail(data: {values: (ApplicationDronesState & {square?: string}) | UralchemRequestValues, type: string}) {
        const url = "/sendBid";
        const config = {method: 'POST'}
        await xhr.fetch(url, {data, ...config});
    }

    static async sendDrones(data: ApplicationDronesState & {square?: string}) {
//         const url = 'https://fly.mikhailichenko.ru/api/fly_request';
        const url = process.env.DRONES_URL || "https://fly.mikhailichenko.ru/api/fly_request";
        const {objectData, formData} = ApplicationService.transformDronesData(data as Required<ApplicationDronesState & {square: string}>);

        const config = {
            headers: {
                'Access-Token': process.env.DRONES_API_KEY || "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpYXQiOjE2OTI3MDgzMTgsIm5iZiI6MTY5MjcwODMxOCwiZXhwIjoyMDA4MzI3NTE4LCJpc3MiOiJodHRwczovL2ZseS5taWtoYWlsaWNoZW5rby5ydSIsImF1ZCI6IjE4IiwianRpIjoiMTgifQ.iW3DlaLALEkgqnJCl1nqjE68CFhu3yfni4Ft4owojmQ",
            },
        }

        await axios.create().post(url, formData, config);
        await ApplicationService.sendToEmail({values: objectData as unknown as (ApplicationDronesState & {square?: string}), type: 'Drone'});
    }

    static transformUralchemData(data: Required<ApplicationUralchemState>) {

        const preparedData: UralchemRequestValues = {
            organizationTitle: data.companyName,
            organizationAddress: data.address,
            organizationPhone: data.phone,
            organizationEmail: data.email,
            analysisObject: data.testObject.label,
            area: String(data.testArea),
            fieldsNumber: String(data.fieldCount),
            samplesNumber: String(data.samplesCount),
            needSamplingService: data.needSelection,
            resultsDate: ApplicationService.transformDateToServerFormat(data.expectedDeadline),
            soilAnalysisType: data.freeSoilTestList || data.testType,
        }

        const formData = new FormData();
        Object.entries(preparedData).forEach(([key, value]) => {
            if (!isNil(value)) formData.append(key, String(value));
        });

        return {objectData: preparedData, formData};
    }

    static async sendUralchem(data: ApplicationUralchemState) {
        const {objectData, formData} = ApplicationService.transformUralchemData(data as Required<ApplicationUralchemState>);
        await ApplicationService.sendToEmail({values: objectData, type: 'UralChem'});
    }

}